import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import Modal from "../../../components/shared/Modal";
import DataTable from "../../../components/shared/DataTable";
import { formatDate } from "../../../utils/helpers";
import api from "../../../utils/api";

export default function Advertisements() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "",
    imageUrl: "",
    linkUrl: "",
    position: "homepage",
    startsAt: "",
    endsAt: "",
  });

  const { data, isLoading } = useQuery("ads", () =>
    api.get("/admin/advertisements")
  );

  const saveMutation = useMutation(
    () =>
      editing
        ? api.put(`/admin/advertisements/${editing.id}`, form)
        : api.post("/admin/advertisements", form),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("ads");
        toast.success("Saved");
        setShowModal(false);
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/admin/advertisements/${id}`),
    { onSuccess: () => queryClient.invalidateQueries("ads") }
  );

  const columns = [
    { key: "title", label: "Title" },
    { key: "position", label: "Position" },
    {
      key: "imageUrl",
      label: "Preview",
      render: (row) =>
        row.imageUrl ? (
          <img
            src={row.imageUrl}
            alt={row.title}
            className="h-10 w-24 object-cover rounded-lg border border-slate-100"
          />
        ) : (
          "—"
        ),
    },
    {
      key: "startsAt",
      label: "Starts",
      render: (row) => formatDate(row.startsAt),
    },
    {
      key: "endsAt",
      label: "Ends",
      render: (row) => formatDate(row.endsAt),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => {
              setEditing(row);
              setForm(row);
              setShowModal(true);
            }}
            className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            Edit
          </button>
          <button
            onClick={() => deleteMutation.mutate(row.id)}
            className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          + Add Advertisement
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        emptyMessage="No advertisements yet."
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Advertisement" : "Add Advertisement"}
        footer={
          <>
            <button onClick={() => setShowModal(false)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isLoading}
              className="btn-primary"
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {[
            { field: "title", label: "Title" },
            { field: "imageUrl", label: "Image URL" },
            { field: "linkUrl", label: "Link URL" },
          ].map(({ field, label }) => (
            <div key={field}>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                {label}
              </label>
              <input
                type="text"
                value={form[field]}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [field]: e.target.value }))
                }
                className="input-field"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              Position
            </label>
            <select
              value={form.position}
              onChange={(e) =>
                setForm((f) => ({ ...f, position: e.target.value }))
              }
              className="input-field"
            >
              <option value="homepage">Homepage</option>
              <option value="sidebar">Sidebar</option>
              <option value="banner">Banner</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                Starts
              </label>
              <input
                type="date"
                value={form.startsAt}
                onChange={(e) =>
                  setForm((f) => ({ ...f, startsAt: e.target.value }))
                }
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                Ends
              </label>
              <input
                type="date"
                value={form.endsAt}
                onChange={(e) =>
                  setForm((f) => ({ ...f, endsAt: e.target.value }))
                }
                className="input-field"
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
